/**
 * Findings over a health snapshot: one line for anything that needs a look.
 *
 * Shared by the two ways a snapshot is taken, so they judge the same way and
 * can be compared line by line:
 *  - `scripts/prod-snapshot.mjs`, run by an operator with admin AWS credentials,
 *    which reads AWS and the table directly;
 *  - the admin console's "Download health report", which reads what the
 *    console's own API returns.
 * A field either source cannot see is simply absent and produces no finding.
 */
const DAY = 86_400_000;
/** SES spells it SUCCESS; the console's API says verified. */
const VERIFIED = new Set(["SUCCESS", "verified"]);
export function healthFindings(snap, now = Date.now()) {
    const out = [];
    const add = (severity, area, message) => out.push({ severity, area, message });
    for (const a of snap.alarms ?? []) {
        if (a.state === "ALARM")
            add("critical", "alarms", `${a.name} is in ALARM${a.since ? ` since ${a.since}` : ""}${a.reason ? `: ${a.reason}` : ""}`);
        else if (a.state === "INSUFFICIENT_DATA")
            add("info", "alarms", `${a.name} has insufficient data`);
    }
    for (const q of snap.queues ?? []) {
        if (q.deadLetter && q.visible > 0)
            add("critical", "queues", `${q.name} holds ${q.visible} dead-lettered message(s)`);
        if (!q.deadLetter && q.visible > 100)
            add("warning", "queues", `${q.name} has ${q.visible} messages waiting`);
    }
    for (const f of snap.lambdas ?? []) {
        if (f.errors24h > 0)
            add("warning", "lambdas", `${f.name}: ${f.errors24h} error(s) in ${f.invocations24h} invocation(s) over 24h`);
        if (f.throttles24h > 0)
            add("warning", "lambdas", `${f.name}: ${f.throttles24h} throttle(s) over 24h`);
    }
    const ses = snap.ses?.account;
    if (ses?.productionAccess === false)
        add("critical", "ses", "SES account is in the sandbox: only verified addresses receive mail");
    if (ses?.sendingEnabled === false)
        add("critical", "ses", "SES sending is disabled for the account");
    if (ses?.enforcementStatus && ses.enforcementStatus !== "HEALTHY")
        add("critical", "ses", `SES enforcement status is ${ses.enforcementStatus}`);
    for (const d of snap.ses?.identities ?? []) {
        if (d.verification !== undefined && !VERIFIED.has(d.verification))
            add("warning", "ses", `Domain ${d.domain} (${d.orgId}) verification: ${d.verification}`);
    }
    for (const o of snap.orgs ?? []) {
        const snapAt = o.audience?.computedAt ? Date.parse(o.audience.computedAt) : NaN;
        if (!Number.isFinite(snapAt))
            add("info", "audience", `${o.orgId}: no nightly audience count yet`);
        else if (now - snapAt > 36 * 3_600_000)
            add("warning", "audience", `${o.orgId}: audience count is ${Math.round((now - snapAt) / 3_600_000)}h old (nightly job may not be running)`);
        if (!o.defaultTimezone || o.defaultTimezone === "UTC")
            add("info", "settings", `${o.orgId}: default time zone is ${o.defaultTimezone ?? "unset"}`);
        for (const s of o.schedules ?? []) {
            if (s.kind === "one_off" && s.status === "active" && s.sendAt && Date.parse(s.sendAt) < now - DAY && s.hasSent === false) {
                add("warning", "schedules", `${o.orgId}/${s.scheduleId}: one-off due ${s.sendAt} is still active and shows no send`);
            }
            if (s.awsScheduleChecked && s.kind === "recurring" && s.status === "active" && !s.awsScheduleState)
                add("warning", "schedules", `${o.orgId}/${s.scheduleId}: active series has no EventBridge schedule`);
            if (s.awsScheduleState === "DISABLED" && s.status === "active")
                add("warning", "schedules", `${o.orgId}/${s.scheduleId}: active in addressium but DISABLED in EventBridge`);
        }
        for (const c of o.campaigns ?? []) {
            const sent = c.counters?.sent ?? 0;
            const bounces = c.counters?.bounces ?? 0;
            const complaints = c.counters?.complaints ?? 0;
            if (sent >= 20 && bounces / sent > 0.05)
                add("warning", "deliverability", `${o.orgId}/${c.campaignId}: bounce rate ${(100 * bounces / sent).toFixed(1)}%`);
            if (sent >= 20 && complaints / sent > 0.001)
                add("warning", "deliverability", `${o.orgId}/${c.campaignId}: complaint rate ${(100 * complaints / sent).toFixed(2)}%`);
            if (c.status === "halted")
                add("warning", "campaigns", `${o.orgId}/${c.campaignId} is halted`);
        }
    }
    return out;
}
//# sourceMappingURL=health-findings.js.map